import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react'; // 🌟 IMPORTED LUCIDE HEART COMPONENT
import { useCart } from '../context/CartContext'; // 🌟 IMPORTED WISHLIST CLOUD HOOK

const ProductCard = ({ id, name, price, discountPrice, images }) => {
  const navigate = useNavigate();
  const mainImage = images && images.length > 0 ? images[0] : null;
  const hoverImage = images && images.length > 1 ? images[1] : null;

  // Pull out wishlist and cart tools from global context provider
  const { toggleWishlist, wishlist, addToCart } = useCart();

  // Check if this specific item ID is already saved in the user's wishlist
  const isWishlisted = wishlist.some(item => item._id === id);

  // Helper check for logged-in user
  const isUserAuthenticated = () => {
    const token = localStorage.getItem('modische_token') || 
                  localStorage.getItem('modische_auth_token') || 
                  localStorage.getItem('token');
    return !!token;
  };

  // 🌟 WISHLIST ACTION WITH AUTH GUARD
  const handleWishlistClick = (e) => {
    e.preventDefault(); // 🔑 STOPS click from opening the product details page
    e.stopPropagation(); // Stops click bubbling

    if (!isUserAuthenticated()) {
      alert('Please sign in to add items to your wishlist.');
      navigate('/login');
      return;
    }

    // Package a minimal object to store inside the wishlist array
    toggleWishlist({ _id: id, name, price, discountPrice, images });
  };

  // 🌟 QUICK ADD TO BAG ACTION WITH AUTH GUARD
  const handleQuickAddToCart = (e) => {
    e.preventDefault(); // Prevent navigating to detail page
    e.stopPropagation();

    if (!isUserAuthenticated()) {
      alert('Please sign in to add items to your bag.');
      navigate('/login');
      return;
    }

    addToCart({ _id: id, name, price: discountPrice || price, image: mainImage, quantity: 1 });
  };

  return (
    <Link to={`/product/${id}`} className="group cursor-pointer block text-left">
      {/* Aspect Container */}
      <div className="relative aspect-[3/4] overflow-hidden bg-[#E5D3B3] mb-4 rounded-sm shadow-sm">
        
        {/* 🌟 ABSOLUTE OVERLAY FAST-ACTION WISHLIST HEART BUTTON */}
        <button
          type="button"
          onClick={handleWishlistClick}
          className="absolute top-3 right-3 z-20 p-2 bg-[#F5F5DC]/80 backdrop-blur-sm rounded-full shadow-md transition-all duration-300 transform hover:scale-110 active:scale-95 group/heart cursor-pointer border-none"
        >
          <Heart 
            className={`w-4 h-4 transition-colors duration-300 ${
              isWishlisted 
                ? 'fill-red-800 text-red-800' 
                : 'text-[#2C1E12] group-hover/heart:text-red-700'
            }`} 
          />
        </button>

        {/* MAIN IMAGE */}
        {mainImage ? (
          <img 
            src={mainImage}
            alt={`${name} Primary View`}
            className={`w-full h-full object-cover transition-all duration-700 absolute inset-0 ${
              hoverImage ? 'group-hover:opacity-0 group-hover:scale-105' : 'group-hover:scale-110'
            }`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#664229]/20 font-bold italic text-xs tracking-widest absolute inset-0">
            MODISCHE PIECE
          </div>
        )}

        {/* SWIPE HOVER IMAGE */}
        {hoverImage && (
          <img 
            src={hoverImage}
            alt={`${name} Secondary View`}
            className="w-full h-full object-cover transition-all duration-700 transform scale-100 group-hover:scale-105 opacity-0 group-hover:opacity-100 absolute inset-0"
          />
        )}

        {/* Slide-Up Action Button */}
        <div className="absolute bottom-0 left-0 w-full p-4 translate-y-full group-hover:translate-y-0 transition-transform bg-[#664229]/10 backdrop-blur-sm z-10">
          <button 
            type="button"
            onClick={handleQuickAddToCart}
            className="w-full py-2 bg-[#664229] text-[#F5F5DC] text-[10px] tracking-widest uppercase font-bold hover:bg-[#2C1E12] transition border-none cursor-pointer"
          >
            Add to Bag
          </button>
        </div>
      </div>

      {/* Item Typography Details */}
      <h3 className="text-xs uppercase tracking-widest text-[#2C1E12] mb-1">{name}</h3>
      
      <div className="text-xs font-medium tracking-tighter">
        {discountPrice ? (
          <p>
            <span className="line-through text-red-700/60 mr-2">₹{price}</span>
            <span className="text-[#2C1E12] font-bold">₹{discountPrice}</span>
          </p>
        ) : (
          <p className="text-[#664229]">₹{price}</p>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;